import React from 'react';
import { Box, Card, CardContent, Paper, Skeleton, Stack } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

/**
 * Placeholders that hold the page still while it loads.
 *
 * The rule every shape here follows: it occupies the same box as the real
 * thing. A spinner tells you to wait but reserves nothing, so the page grows
 * under the reader the moment the answer arrives — which is what made
 * Vie de Quartier feel like it was assembling itself in front of you. These are
 * sized from the components they stand in for, so the layout settles once,
 * before the data, and never moves again.
 */

/**
 * Wraps a loading area so assistive technology is told what is happening.
 *
 * The shapes themselves are decoration and are hidden; the sentence is what
 * gets announced.
 */
export function Loading({ label = 'Chargement en cours', children }) {
  return (
    <Box aria-busy="true" aria-live="polite">
      <Box component="span" sx={visuallyHidden}>
        {label}
      </Box>
      <Box aria-hidden="true">{children}</Box>
    </Box>
  );
}

/** Repeats a shape, because every list here is a list of the same thing. */
function repeat(count, render) {
  return Array.from({ length: count }, (unused, index) => render(index));
}

/** Stands in for `ProviderCard`: vignette, name, rating, blurb, phone, button. */
export function ProviderCardSkeleton() {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Skeleton variant="rectangular" height={108} sx={{ borderRadius: 0 }} />
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flexGrow: 1, p: 2.75 }}>
        <Skeleton variant="text" width="70%" height={30} />
        <Skeleton variant="text" width="45%" height={20} />
        <Skeleton variant="text" width="55%" height={20} />
        <Box sx={{ flexGrow: 1, pt: 0.5 }}>
          <Skeleton variant="text" height={18} />
          <Skeleton variant="text" width="80%" height={18} />
        </Box>
        <Skeleton variant="text" width="50%" height={24} />
        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 999 }} />
      </CardContent>
    </Card>
  );
}

/** Stands in for `PostCard`. */
export function PostCardSkeleton() {
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2.5, sm: 3.25 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Stack direction="row" sx={{ gap: 1 }}>
          <Skeleton variant="rectangular" width={96} height={30} sx={{ borderRadius: 999 }} />
          <Skeleton variant="rectangular" width={120} height={30} sx={{ borderRadius: 999 }} />
        </Stack>
        <Skeleton variant="text" width="65%" height={34} />
        <Skeleton variant="text" height={20} />
        <Skeleton variant="text" width="85%" height={20} />
        <Stack
          direction="row"
          sx={{ alignItems: 'center', gap: 1.25, pt: 1.75, mt: 0.25, borderTop: '1px solid', borderColor: 'surface.rule' }}
        >
          <Skeleton variant="circular" width={38} height={38} />
          <Skeleton variant="text" width={180} height={18} />
        </Stack>
      </CardContent>
    </Card>
  );
}

/** Stands in for `EventCard`: the date block plus its lines. */
export function EventCardSkeleton() {
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 }, display: 'flex', gap: 2.5 }}>
        <Skeleton variant="rectangular" width={64} height={68} sx={{ flexShrink: 0, borderRadius: '14px' }} />
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Skeleton variant="rectangular" width={140} height={30} sx={{ borderRadius: 999 }} />
          <Skeleton variant="text" width="60%" height={30} />
          <Skeleton variant="text" width="45%" height={22} />
          <Skeleton variant="text" width="35%" height={22} />
        </Box>
      </CardContent>
    </Card>
  );
}

/** Stands in for one row of an appointment list. */
export function BookingCardSkeleton() {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, display: 'flex', gap: 2.5 }}>
      <Skeleton
        variant="rectangular"
        width={64}
        height={68}
        sx={{ flexShrink: 0, borderRadius: '14px', display: { xs: 'none', sm: 'block' } }}
      />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Skeleton variant="rectangular" width={110} height={30} sx={{ borderRadius: 999 }} />
        <Skeleton variant="text" width="55%" height={30} />
        <Skeleton variant="text" width="40%" height={22} />
        <Skeleton variant="text" width="60%" height={22} />
      </Box>
    </Paper>
  );
}

/** Stands in for one chore in `MyTasks`. */
export function TaskRowSkeleton() {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', gap: 1.5 }}>
      <Skeleton variant="rectangular" width={24} height={24} sx={{ flexShrink: 0, mt: 0.5 }} />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Skeleton variant="text" width="50%" height={30} />
        <Stack direction="row" sx={{ gap: 1 }}>
          <Skeleton variant="rectangular" width={110} height={30} sx={{ borderRadius: 999 }} />
          <Skeleton variant="rectangular" width={90} height={30} sx={{ borderRadius: 999 }} />
        </Stack>
      </Box>
    </Paper>
  );
}

/** A framed block of text, for a panel whose exact shape does not matter. */
export function PanelSkeleton({ lines = 3, height }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 } }}>
      <Skeleton variant="text" width="35%" height={34} sx={{ mb: 2 }} />
      {height ? (
        <Skeleton variant="rectangular" height={height} />
      ) : (
        repeat(lines, (index) => (
          <Skeleton key={index} variant="text" height={20} width={index === lines - 1 ? '70%' : '100%'} />
        ))
      )}
    </Paper>
  );
}

/** A few plain lines, for the digests on the home page. */
export function RowsSkeleton({ count = 3 }) {
  return (
    <>
      {repeat(count, (index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.9,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'surface.rule',
            '&:last-of-type': { borderBottom: 0 },
          }}
        >
          <Skeleton variant="rectangular" width={90} height={26} sx={{ borderRadius: 999 }} />
          <Skeleton variant="text" width="80%" height={24} />
          <Skeleton variant="text" width="55%" height={18} />
        </Box>
      ))}
    </>
  );
}

/** A grid of listing cards, sized for the directory's three columns. */
export function ProviderGridSkeleton({ count = 6 }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
        gap: 2.75,
      }}
    >
      {repeat(count, (index) => (
        <ProviderCardSkeleton key={index} />
      ))}
    </Box>
  );
}

/** A stack of identical shapes, for the feed, the agenda and the pro lists. */
export function StackSkeleton({ count = 3, gap = 2.25, children: Shape }) {
  return <Stack spacing={gap}>{repeat(count, (index) => <Shape key={index} />)}</Stack>;
}
